import * as XLSX from 'xlsx';
import { supabase } from './supabase';
import { selectAll } from './data';

// ── НОРМАЛИЗАЦИЯ ─────────────────────────────────────────────────────────────
export const normPhone = (v) => {
  const d = String(v == null ? '' : v).replace(/\D/g, '');
  return d.length >= 10 ? d.slice(-10) : '';
};
export const normEmail = (v) => {
  const s = String(v == null ? '' : v).trim().toLowerCase();
  return s.includes('@') ? s : '';
};
export const normSnils = (v) => {
  const d = String(v == null ? '' : v).replace(/\D/g, '');
  return d.length >= 9 ? d : '';
};
export const nameTokens = (v) =>
  String(v == null ? '' : v).toLowerCase().replace(/ё/g, 'е')
    .replace(/[^а-яa-z\s-]/g, ' ').replace(/-/g, ' ')
    .split(/\s+/).filter(t => t.length > 1);
const nameKey = (v) => nameTokens(v).slice().sort().join(' ');
const isEmptyName = (s) => {
  const t = String(s || '').trim().toLowerCase();
  return !t || /^(x|х|xx|хх|-|—)$/.test(t) || t.startsWith('вакан') || t.includes('итог');
};

// ── СВЕРКА ───────────────────────────────────────────────────────────────────
// Реестр → индексы. Совпадение по любому из ключей: СНИЛС → телефон → почта → фамилия+имя.
export function buildRegistryIndex(registry) {
  const idx = { snils: new Map(), phone: new Map(), email: new Map(), tok: new Map(), rows: registry };
  registry.forEach((r, i) => {
    if (r.snils) idx.snils.set(r.snils, i);
    if (r.phone) idx.phone.set(r.phone, i);
    if (r.email) idx.email.set(r.email, i);
    r._tokens = nameTokens(r.full_name);
    r._tokens.forEach(t => { if (!idx.tok.has(t)) idx.tok.set(t, []); idx.tok.get(t).push(i); });
  });
  return idx;
}

export function matchPerson(p, idx) {
  const sn = normSnils(p.snils), ph = normPhone(p.phone), em = normEmail(p.email);
  if (sn && idx.snils.has(sn)) return { reg: idx.rows[idx.snils.get(sn)], by: 'СНИЛС' };
  if (ph && idx.phone.has(ph)) return { reg: idx.rows[idx.phone.get(ph)], by: 'Телефон' };
  if (em && idx.email.has(em)) return { reg: idx.rows[idx.email.get(em)], by: 'Почта' };
  const A = nameTokens(p.name);
  if (A.length >= 2) {
    const cand = new Set();
    A.forEach(t => (idx.tok.get(t) || []).forEach(i => cand.add(i)));
    const aSet = new Set(A);
    for (const i of cand) {
      const B = idx.rows[i]._tokens;
      if (B.length < 2) continue;
      const bSet = new Set(B);
      const common = A.filter(t => bSet.has(t)).length;
      if (common >= 2 && (A.every(t => bSet.has(t)) || B.every(t => aSet.has(t)))) {
        return { reg: idx.rows[i], by: 'Фамилия и имя' };
      }
    }
  }
  return null;
}

// ── ДАННЫЕ ───────────────────────────────────────────────────────────────────
export async function loadRegistry() {
  return selectAll('referral_registry', '*');
}

// Структура: ТМ → СВ → мерчендайзеры (из таблиц Field Hub, без отдельного файла).
export async function loadStructure() {
  const [tmsRaw, svsRaw, routes] = await Promise.all([
    selectAll('users', 'id, full_name', q => q.eq('role', 'tm').eq('is_active', true)),
    selectAll('supervisors', 'id, full_name, internal_code, tm_id, user_id'),
    selectAll('routes', 'id, internal_code, is_vacancy, is_hidden, is_active, sv_id, merchandisers(full_name, phone, email, is_vacancy)',
      q => q.eq('is_active', true)),
  ]);
  const tms = tmsRaw.map(t => ({ id: t.id, name: t.full_name })).sort((a, b) => a.name.localeCompare(b.name, 'ru'));
  const svs = svsRaw.map(s => ({ id: s.id, user_id: s.user_id, tm_id: s.tm_id, name: s.full_name, code: s.internal_code }));
  const mrcs = [];
  routes.forEach(r => {
    if (r.is_vacancy || r.is_hidden) return;
    const m = Array.isArray(r.merchandisers) ? r.merchandisers[0] : r.merchandisers;
    if (!m || m.is_vacancy || isEmptyName(m.full_name)) return;
    mrcs.push({ route_code: r.internal_code, sv_id: r.sv_id, name: m.full_name, phone: m.phone || '', email: m.email || '' });
  });
  return { tms, svs, mrcs };
}

export function personKey(m) {
  return normPhone(m.phone) || normEmail(m.email) || nameKey(m.name);
}

// Добавляет к каждому мерчендайзеру connected/by/regName и tm_id.
export function reconcile(structure, registry) {
  const idx = buildRegistryIndex(registry.map(r => ({ ...r })));
  const svById = new Map(structure.svs.map(s => [s.id, s]));
  return structure.mrcs.map(m => {
    const hit = matchPerson(m, idx);
    const sv = svById.get(m.sv_id);
    return {
      ...m,
      key: personKey(m),
      tm_id: sv ? sv.tm_id : null,
      connected: !!hit,
      by: hit ? hit.by : '',
      regName: hit ? hit.reg.full_name : '',
    };
  });
}

export const pctOf = (conn, total) => (total ? Math.round((conn * 100) / total) : 0);

export function groupStats(list, keyFn) {
  const map = new Map();
  list.forEach(m => {
    const k = keyFn(m);
    if (k == null) return;
    const g = map.get(k) || { key: k, total: 0, conn: 0 };
    g.total++; if (m.connected) g.conn++;
    map.set(k, g);
  });
  return Array.from(map.values()).map(g => ({ ...g, pct: pctOf(g.conn, g.total) }))
    .sort((a, b) => b.pct - a.pct || b.conn - a.conn);
}

// ── СНИМКИ И ДИНАМИКА ────────────────────────────────────────────────────────
export async function saveSnapshot(list) {
  const conn = list.filter(m => m.connected).length;
  const { data, error } = await supabase.from('referral_snapshots')
    .insert({ total: list.length, connected: conn }).select().single();
  if (error) throw error;
  const rows = list.map(m => ({
    snapshot_id: data.id, person_key: m.key || '', full_name: m.name, route_code: m.route_code,
    tm_id: m.tm_id, sv_id: m.sv_id, connected: m.connected,
  }));
  for (let i = 0; i < rows.length; i += 500) {
    const { error: e2 } = await supabase.from('referral_snapshot_rows').insert(rows.slice(i, i + 500));
    if (e2) throw e2;
  }
  return data;
}

export async function listSnapshots() {
  const { data, error } = await supabase.from('referral_snapshots').select('*').order('taken_at', { ascending: false }).limit(60);
  if (error) throw error;
  return data || [];
}

export async function loadSnapshotRows(id) {
  return selectAll('referral_snapshot_rows', 'person_key, full_name, tm_id, sv_id, connected', q => q.eq('snapshot_id', id));
}

// Сравнение текущей сверки со снимком. change: 'new' | 'off' | ''
export function applyDynamics(list, snapRows) {
  const prev = new Map();
  snapRows.forEach(r => prev.set(r.person_key, r.connected));
  const seen = new Set();
  let added = 0, off = 0, was = 0;
  const out = list.map(m => {
    seen.add(m.key);
    const p = prev.get(m.key);
    let change = '';
    if (m.connected && p === false) { change = 'new'; added++; }
    else if (m.connected && p === undefined) { change = 'new'; added++; }
    else if (!m.connected && p === true) { change = 'off'; off++; }
    return { ...m, change };
  });
  snapRows.forEach(r => { if (r.connected) was++; });
  const left = snapRows.filter(r => !seen.has(r.person_key)).length;
  return { list: out, was, now: out.filter(m => m.connected).length, added, off, left };
}

// ── ИМПОРТ РЕЕСТРА ───────────────────────────────────────────────────────────
function readRows(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        resolve(XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, defval: '' }));
      } catch (err) { reject(err); }
    };
    reader.onerror = reject;
    reader.readAsBinaryString(file);
  });
}

// Колонки определяем по заголовкам (первые 8 строк). Колонка «Проект» и даты игнорируются.
export async function parseRegistryFile(file) {
  const rows = await readRows(file);
  const h = (v) => String(v || '').toLowerCase().replace(/ё/g, 'е');
  let hi = -1, cols = null;
  for (let i = 0; i < Math.min(rows.length, 8); i++) {
    const hs = rows[i].map(h);
    const find = (...keys) => hs.findIndex(x => x && keys.some(k => x.includes(k)));
    const phone = find('тел', 'phone', 'моб');
    const fio = hs.findIndex(x => x && (x === 'фио' || x.includes('фио') || x.includes('ф.и.о')));
    const sur = find('фамил');
    const nm = hs.findIndex(x => x && (x === 'имя' || x.startsWith('имя')));
    if (phone >= 0 || fio >= 0 || (sur >= 0 && nm >= 0)) {
      hi = i;
      cols = {
        fio, sur, nm, phone,
        email: find('mail', 'почт'), snils: find('снилс', 'snils'),
        bonus: find('бонус', 'начисл'),
      };
      break;
    }
  }
  if (!cols) throw new Error('Не нашёл строку заголовков: нужны колонки ФИО (или Фамилия и Имя) и телефон.');
  const out = [];
  rows.slice(hi + 1).forEach(r => {
    let name = cols.fio >= 0 ? String(r[cols.fio] || '').trim() : '';
    if (!name && cols.sur >= 0) name = [r[cols.sur], cols.nm >= 0 ? r[cols.nm] : ''].map(x => String(x || '').trim()).filter(Boolean).join(' ');
    const phone = cols.phone >= 0 ? normPhone(r[cols.phone]) : '';
    const email = cols.email >= 0 ? normEmail(r[cols.email]) : '';
    const snils = cols.snils >= 0 ? normSnils(r[cols.snils]) : '';
    if (!name && !phone && !email && !snils) return;
    const b = cols.bonus >= 0 ? parseFloat(String(r[cols.bonus]).replace(/\s/g, '').replace(',', '.')) : NaN;
    out.push({ full_name: name, phone: phone || null, email: email || null, snils: snils || null, bonus: isNaN(b) ? null : b });
  });
  return out;
}

// Вставка/обновление без дублей: ищем существующую запись по снилс/телефон/почта/ФИО.
export async function importRegistry(newRows) {
  const existing = await loadRegistry();
  const idx = buildRegistryIndex(existing.map(r => ({ ...r })));
  const owner = { phone: new Map(), email: new Map(), snils: new Map() };
  existing.forEach(r => {
    if (r.phone) owner.phone.set(r.phone, r.id);
    if (r.email) owner.email.set(r.email, r.id);
    if (r.snils) owner.snils.set(r.snils, r.id);
  });
  const updates = new Map();   // id -> запись
  const inserts = [];
  const pendingKey = { phone: new Map(), email: new Map(), snils: new Map() };
  let skipped = 0;

  for (const row of newRows) {
    if (!row.full_name && !row.phone && !row.email && !row.snils) { skipped++; continue; }
    const hit = matchPerson({ name: row.full_name, phone: row.phone, email: row.email, snils: row.snils }, idx);
    const clean = { ...row };
    if (hit) {
      const cur = updates.get(hit.reg.id) || { ...hit.reg };
      ['phone', 'email', 'snils'].forEach(k => {
        if (!clean[k]) return;
        const o = owner[k].get(clean[k]);
        if (o && o !== cur.id) return;          // ключ уже у другой записи — не трогаем
        cur[k] = clean[k]; owner[k].set(clean[k], cur.id);
      });
      if (clean.full_name) cur.full_name = clean.full_name;
      if (clean.bonus != null) cur.bonus = clean.bonus;
      updates.set(cur.id, cur);
    } else {
      ['phone', 'email', 'snils'].forEach(k => {
        if (clean[k] && (owner[k].has(clean[k]) || pendingKey[k].has(clean[k]))) clean[k] = null;
      });
      ['phone', 'email', 'snils'].forEach(k => { if (clean[k]) pendingKey[k].set(clean[k], true); });
      if (!clean.full_name) { skipped++; continue; }
      inserts.push(clean);
    }
  }
  const upd = Array.from(updates.values()).map(r => ({
    id: r.id, full_name: r.full_name, phone: r.phone || null, email: r.email || null, snils: r.snils || null,
    bonus: r.bonus == null ? null : r.bonus, updated_at: new Date().toISOString(),
  }));
  for (let i = 0; i < upd.length; i += 200) {
    // по одной записи в обновляемых ключах конфликты исключены выше
    const { error } = await supabase.from('referral_registry').upsert(upd.slice(i, i + 200), { onConflict: 'id' });
    if (error) throw error;
  }
  for (let i = 0; i < inserts.length; i += 200) {
    const { error } = await supabase.from('referral_registry').insert(inserts.slice(i, i + 200));
    if (error) throw error;
  }
  return { added: inserts.length, updated: upd.length, skipped };
}

export async function saveRegistryRecord(rec) {
  const row = {
    full_name: String(rec.full_name || '').trim(),
    phone: normPhone(rec.phone) || null,
    email: normEmail(rec.email) || null,
    snils: normSnils(rec.snils) || null,
    bonus: rec.bonus === '' || rec.bonus == null ? null : Number(rec.bonus),
    updated_at: new Date().toISOString(),
  };
  if (!row.full_name) throw new Error('Укажите фамилию и имя');
  const q = rec.id
    ? supabase.from('referral_registry').update(row).eq('id', rec.id)
    : supabase.from('referral_registry').insert(row);
  const { error } = await q;
  if (error) throw new Error(error.code === '23505' ? 'Такой телефон, почта или СНИЛС уже есть в реестре' : error.message);
}

export async function deleteRegistryRecord(id) {
  const { error } = await supabase.from('referral_registry').delete().eq('id', id);
  if (error) throw error;
}

// ── EXCEL ────────────────────────────────────────────────────────────────────
export function exportReferralExcel({ list, structure, dyn }) {
  const tmName = new Map(structure.tms.map(t => [t.id, t.name]));
  const svById = new Map(structure.svs.map(s => [s.id, s]));
  const chLabel = (c) => (c === 'new' ? 'Новое подключение' : c === 'off' ? 'Отключился' : '');
  const sorted = [...list].sort((a, b) =>
    String(tmName.get(a.tm_id) || '').localeCompare(String(tmName.get(b.tm_id) || ''), 'ru') ||
    String((svById.get(a.sv_id) || {}).name || '').localeCompare(String((svById.get(b.sv_id) || {}).name || ''), 'ru') ||
    a.name.localeCompare(b.name, 'ru'));

  const head = ['ТМ', 'СВ', 'Маршрут', 'ФИО (табель)', 'Телефон', 'Почта', 'Статус', 'Совпадение по', 'ФИО (из реестра)'];
  if (dyn) head.push('Изменение');
  const s1 = [head, ...sorted.map(m => {
    const row = [tmName.get(m.tm_id) || '', (svById.get(m.sv_id) || {}).name || '', m.route_code, m.name, m.phone, m.email,
      m.connected ? 'Подключён' : 'Не подключён', m.by, m.regName];
    if (dyn) row.push(chLabel(m.change));
    return row;
  })];

  // Сводка ТМ → СВ с итогами
  const s2 = [['ТМ', 'СВ', 'Всего', 'Подключено', 'Не подключено', '% подключения']];
  let T = 0, C = 0;
  groupStats(list, m => m.tm_id || '—').sort((a, b) =>
    String(tmName.get(a.key) || '').localeCompare(String(tmName.get(b.key) || ''), 'ru')).forEach(tg => {
    const inTm = list.filter(m => (m.tm_id || '—') === tg.key);
    s2.push([tmName.get(tg.key) || 'Без ТМ', 'ИТОГО ТМ', tg.total, tg.conn, tg.total - tg.conn, tg.pct / 100]);
    groupStats(inTm, m => m.sv_id || '—').forEach(sg => {
      s2.push(['', (svById.get(sg.key) || {}).name || 'Без СВ', sg.total, sg.conn, sg.total - sg.conn, sg.pct / 100]);
    });
    T += tg.total; C += tg.conn;
  });
  s2.push(['ИТОГО', '', T, C, T - C, T ? C / T : 0]);

  const wb = XLSX.utils.book_new();
  const w1 = XLSX.utils.aoa_to_sheet(s1);
  w1['!cols'] = [{ wch: 22 }, { wch: 22 }, { wch: 16 }, { wch: 32 }, { wch: 16 }, { wch: 26 }, { wch: 14 }, { wch: 16 }, { wch: 32 }, { wch: 18 }];
  w1['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: s1.length - 1, c: head.length - 1 } }) };
  w1['!freeze'] = { xSplit: 0, ySplit: 1 };
  XLSX.utils.book_append_sheet(wb, w1, 'Приведи друга');
  const w2 = XLSX.utils.aoa_to_sheet(s2);
  w2['!cols'] = [{ wch: 24 }, { wch: 24 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 14 }];
  for (let r = 1; r < s2.length; r++) { const c = w2[XLSX.utils.encode_cell({ r, c: 5 })]; if (c) c.z = '0%'; }
  XLSX.utils.book_append_sheet(wb, w2, 'Сводка');
  if (dyn) {
    const s3 = [['Показатель', 'Значение'], ['Было подключено', dyn.was], ['Стало подключено', dyn.now],
      ['Δ', dyn.now - dyn.was], ['Новые подключения', dyn.added], ['Отключились', dyn.off], ['Выбыли из табеля', dyn.left]];
    const w3 = XLSX.utils.aoa_to_sheet(s3); w3['!cols'] = [{ wch: 24 }, { wch: 12 }];
    XLSX.utils.book_append_sheet(wb, w3, 'Динамика');
  }
  const d = new Date(); const pad = (n) => String(n).padStart(2, '0');
  XLSX.writeFile(wb, `Приведи_друга_${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}.xlsx`);
}
